import {
    ROLE,
    SALE_ORDER_STATUS,
    WORKSHOP,
} from "../../data/enums";
import { ProductOrderFilterType, valueToText } from "../../configs/filterTypes";

export const RESERVE_CREATE_ROLES: ROLE[] = [
    ROLE.MANAGER,
    ROLE.PRODUCTION_STAFF,
    ROLE.FACTORY_MANAGER_ONE,
    ROLE.FACTORY_MANAGER_TWO,
    ROLE.DEPUTY_FACTORY_MANAGER_ONE,
    ROLE.DEPUTY_FACTORY_MANAGER_TWO,
];

export const RESERVE_COORDINATOR_ASSIGNMENT_ROLES = [
    "production_staff",
    "logistic_manager",
];

export const RESERVE_STORAGE_ASSIGNMENT_ROLES = [
    "first_storage_keeper",
    "second_storage_keeper",
];

const getAssignmentRowsForRoles = (
    assignments: any,
    roles: string[],
): any[] => {
    if (!assignments) return [];

    if (Array.isArray(assignments)) {
        return assignments.filter((assignment) =>
            roles.includes(String(assignment?.role || "").toLowerCase()),
        );
    }

    const assignmentSource = assignments?.results || assignments;
    if (Array.isArray(assignmentSource)) {
        return assignmentSource.filter((assignment) =>
            roles.includes(String(assignment?.role || "").toLowerCase()),
        );
    }
    if (
        !assignmentSource ||
        typeof assignmentSource !== "object" ||
        Array.isArray(assignmentSource)
    ) {
        return [];
    }

    return roles.flatMap((role) => {
        const rows =
            assignmentSource[role] ||
            assignmentSource[role.toUpperCase()] ||
            [];
        return Array.isArray(rows) ? rows : [];
    });
};

export const getReserveStorageAssignments = (assignments: any) =>
    getAssignmentRowsForRoles(
        assignments,
        RESERVE_STORAGE_ASSIGNMENT_ROLES,
    );

export const hasReserveStorageAssignment = (assignments: any) =>
    getReserveStorageAssignments(assignments).length > 0;

export const isReserveStorageAssignee = (
    assignments: any,
    userId?: string | null,
) =>
    Boolean(
        userId &&
            getReserveStorageAssignments(assignments).some(
                (assignment) =>
                    String(
                        assignment?.user_id ||
                            assignment?.user?.id ||
                            assignment?.user?.user_id ||
                            "",
                    ) === String(userId),
            ),
    );

export const isReserveImportAreaVisible = (status?: string | null) =>
    Object.values(SALE_ORDER_STATUS).includes(
        String(status) as SALE_ORDER_STATUS,
    );

export const canManageReserveImportRequest = (status?: string | null) =>
    [
        SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING,
        SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING,
    ].includes(String(status) as SALE_ORDER_STATUS);

export const getReserveImportActionMessage = (
    status?: string | null,
    canImport = false,
) => {
    if (canImport) return "";
    return String(status) === SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING
        ? "Thủ kho được bàn giao thực hiện"
        : "Chưa tới bước nhập kho";
};

export const getWorkshopForRole = (role: ROLE): WORKSHOP | null => {
    if (
        [ROLE.FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_ONE].includes(
            role,
        )
    ) {
        return WORKSHOP.BARREL_VESSEL;
    }
    if (
        [ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(
            role,
        )
    ) {
        return WORKSHOP.AUTOMOTIVE;
    }
    return null;
};

export const getWorkshopLabel = (workshop?: string | null) => {
    if (workshop === WORKSHOP.AUTOMOTIVE) return "Xưởng Cơ khí ô tô";
    if (workshop === WORKSHOP.BARREL_VESSEL) return "Xưởng Phuy/Thùng";
    return "Chưa chọn xưởng";
};

export const getWorkshopCategories = (workshop?: string | null) => {
    if (workshop === WORKSHOP.AUTOMOTIVE) return ["Cơ Khí Ô Tô"];
    if (workshop === WORKSHOP.BARREL_VESSEL) return ["Phuy", "Thùng"];
    return [];
};

export const canManageReserveAssignments = (
    role: ROLE,
    workshop?: string | null,
) => {
    if ([ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(role)) return true;
    const roleWorkshop = getWorkshopForRole(role);
    return Boolean(roleWorkshop && roleWorkshop === workshop);
};

export const getOrderTypeLabel = (orderType?: string | null) =>
    orderType === "RESERVE" ? "Đơn dự phòng" : "Đơn bán hàng";

export const getSaleOrderStatusLabel = (
    status?: string | null,
    isReserve = false,
) => {
    if (
        isReserve &&
        status === SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING
    ) {
        return "Nhập kho";
    }
    return (
        valueToText(ProductOrderFilterType, "status", status || "") ||
        status ||
        ""
    );
};
